import React, { useState, useCallback, useEffect, useRef } from "react";
import { Home, Plus, Check, Mail, Phone, User as UserIcon, AlertCircle, Camera, Pencil, X, ShieldCheck, Trash2 } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card } from "../../components/ui/Card";
import { GoogleAddressAutocomplete } from "../../components/address/GoogleAddressAutocomplete";
import { useProperty } from "../../contexts/PropertyContext";
import { useAuthStore } from "../../store/authStore";
import { apiFetch } from "../../services/api";
import { API_ENDPOINTS } from "../../config/api-endpoints";
import { createHome, deleteHome, updateHome } from "../../services/home.api";
import { EditHomeModal } from '../../components/modals/EditHomeModal';

function formatMemberSince(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", { month: "long", year: "numeric" });
}


// ── Isolated Add Property Modal (avoids re-rendering parent on every keystroke) ──
function AddPropertyModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => Promise<void> }) {
  const { t } = useTranslation('profile');
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('United States');

  const handleSubmit = async () => {
    if (!address || !city || !country) {
      setError(t('modal.error.required'));
      return;
    }

    setIsAdding(true);
    setError('');

    try {
      await createHome({
        name: name || `${city} Home`,
        address,
        city,
        state,
        zip,
        country,
      });

      setSuccess(true);
      await onSuccess();

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: unknown) {
      console.error('Failed to add property:', err);
      setError(err instanceof Error ? err.message : t('modal.error.failed'));
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="bg-background border rounded-2xl shadow-lg w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-xl font-medium mb-4">{t('modal.title')}</h3>

          {success ? (
            <div className="text-center py-8">
              <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-lg font-medium text-green-600">{t('modal.success')}</p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">{t('modal.homeName')}</label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('modal.homeNamePlaceholder')}
                    disabled={isAdding}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('modal.nameHint', { city: city || 'City' })}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">{t('modal.address')}</label>
                  <GoogleAddressAutocomplete
                    value={address}
                    onChange={setAddress}
                    onPlaceSelected={(parsed) => {
                      if (parsed.city) setCity(parsed.city);
                      if (parsed.state) setState(parsed.state);
                      if (parsed.zipCode) setZip(parsed.zipCode);
                      if (parsed.country) setCountry(parsed.country);
                    }}
                    placeholder={t('modal.addressPlaceholder')}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                    disabled={isAdding}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('modal.city')}</label>
                    <Input
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder={t('modal.cityPlaceholder')}
                      disabled={isAdding}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('modal.state')}</label>
                    <Input
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      placeholder={t('modal.statePlaceholder')}
                      disabled={isAdding}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('modal.zipCode')}</label>
                    <Input
                      value={zip}
                      onChange={(e) => setZip(e.target.value)}
                      placeholder={t('modal.zipPlaceholder')}
                      disabled={isAdding}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('modal.country')}</label>
                    <select
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      disabled={isAdding}
                      className="w-full px-4 py-2.5 rounded-xl border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
                    >
                      <option value="United States">{t('modal.countries.unitedStates')}</option>
                      <option value="Canada">{t('modal.countries.canada')}</option>
                      <option value="United Kingdom">{t('modal.countries.unitedKingdom')}</option>
                      <option value="Australia">{t('modal.countries.australia')}</option>
                      <option value="India">{t('modal.countries.india')}</option>
                      <option value="Germany">{t('modal.countries.germany')}</option>
                      <option value="France">{t('modal.countries.france')}</option>
                      <option value="Spain">{t('modal.countries.spain')}</option>
                      <option value="Italy">{t('modal.countries.italy')}</option>
                      <option value="Mexico">{t('modal.countries.mexico')}</option>
                      <option value="Brazil">{t('modal.countries.brazil')}</option>
                      <option value="Japan">{t('modal.countries.japan')}</option>
                      <option value="China">{t('modal.countries.china')}</option>
                      <option value="Singapore">{t('modal.countries.singapore')}</option>
                      <option value="United Arab Emirates">{t('modal.countries.unitedArabEmirates')}</option>
                      <option value="Other">{t('modal.countries.other')}</option>
                    </select>
                  </div>
                </div>
              </div>

              {error && (
                <div className="mt-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={isAdding}
                  className="flex-1"
                >
                  {t('modal.cancel')}
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!address || !city || !country || isAdding}
                  className="flex-1"
                >
                  {isAdding ? (
                    <span className="flex items-center gap-2">
                      <span className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      {t('modal.adding')}
                    </span>
                  ) : (
                    t('modal.addHome')
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function Profile() {
  const { t } = useTranslation('profile');
  const navigate = useNavigate();
  const location = useLocation();
  const { currentProperty, properties, switchProperty, refreshProperties } = useProperty();
  const { user, fetchUser } = useAuthStore();
  // ── Profile edit state ──────────────────────────────────────────────────────
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [profileNameError, setProfileNameError] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // ── Phone change state ──────────────────────────────────────────────────────
  // phoneStep: 0=display, 1=enter-new-phone, 2=enter-otp
  const [phoneStep, setPhoneStep] = useState<0 | 1 | 2>(0);
  const [newPhone, setNewPhone] = useState('');
  const [phoneOtp, setPhoneOtp] = useState('');
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  // ── Other state ─────────────────────────────────────────────────────────────
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [editingHome, setEditingHome] = useState(null as any);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deletingHomeId, setDeletingHomeId] = useState(null as any);
  const [deleting, setDeleting] = useState(false);

  const openAddModal = useCallback(() => {
    setShowAddPropertyModal(true);
  }, []);

  const closeAddModal = useCallback(() => {
    setShowAddPropertyModal(false);
  }, []);

  // ── Phone change handlers ───────────────────────────────────────────────────
  const handleRequestPhone = useCallback(async () => {
    const trimmed = newPhone.trim();
    if (!trimmed) { setPhoneError(t('phone.error.enter')); return; }
    if (!/^\+?[\d\s\-().]{7,20}$/.test(trimmed)) { setPhoneError(t('phone.error.invalid')); return; }
    setPhoneSaving(true); setPhoneError('');
    try {
      await apiFetch(API_ENDPOINTS.USERS.PHONE_REQUEST_CHANGE, { method: 'POST', body: { newPhone: trimmed } });
      setPhoneStep(2);
    } catch (e: unknown) {
      setPhoneError(e instanceof Error ? e.message : 'Failed to send OTP. Please try again.');
    } finally { setPhoneSaving(false); }
  }, [newPhone]);

  const handleVerifyPhone = useCallback(async () => {
    const trimmedOtp = phoneOtp.trim();
    if (!trimmedOtp) { setPhoneError(t('phone.error.otpRequired')); return; }
    setPhoneSaving(true); setPhoneError('');
    try {
      await apiFetch(API_ENDPOINTS.USERS.PHONE_VERIFY_CHANGE, { method: 'POST', body: { newPhone: newPhone.trim(), otp: trimmedOtp } });
      await fetchUser();
      setPhoneStep(0); setNewPhone(''); setPhoneOtp(''); setPhoneError('');
    } catch (e: unknown) {
      setPhoneError(e instanceof Error ? e.message : 'Verification failed. Please try again.');
    } finally { setPhoneSaving(false); }
  }, [newPhone, phoneOtp, fetchUser]);

  const handleCancelPhone = useCallback(() => {
    setPhoneStep(0); setNewPhone(''); setPhoneOtp(''); setPhoneError('');
  }, []);

  // ── Profile edit handlers ───────────────────────────────────────────────────
  const handleEditProfile = useCallback(() => {    setProfileName(user?.name ?? '');
    setProfileNameError('');
    setProfileError('');
    setAvatarFile(null);
    setAvatarPreview(null);
    setIsEditingProfile(true);
    // Also enter phone editing mode
    setPhoneStep(1);
    setNewPhone(user?.phone ?? '');
    setPhoneError('');
  }, [user]);

  const handleCancelEdit = useCallback(() => {
    setIsEditingProfile(false);
    setProfileName('');
    setProfileNameError('');
    setProfileError('');
    setAvatarFile(null);
    setAvatarPreview(null);
    // Also cancel phone editing
    setPhoneStep(0);
    setNewPhone('');
    setPhoneOtp('');
    setPhoneError('');
  }, []);

  const handleAvatarChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  }, []);

  const handleSaveProfile = useCallback(async () => {
    const trimmedName = profileName.trim();
    if (!trimmedName) {
      setProfileNameError(t('nameError.empty'));
      return;
    }
    if (!/^[a-zA-Z\s'-]{1,100}$/.test(trimmedName)) {
      setProfileNameError(t('nameError.invalid'));
      return;
    }
    setIsSavingProfile(true);
    setProfileError('');
    setProfileNameError('');
    try {
      const body: Record<string, string> = { name: trimmedName };
      if (avatarFile && avatarPreview) {
        body.avatarUrl = avatarPreview; // base64 data URL stored as avatarUrl
      }
      await apiFetch(API_ENDPOINTS.USERS.UPDATE_ME, { method: 'PUT', body });
      await fetchUser();
      setIsEditingProfile(false);
      setAvatarFile(null);
      setAvatarPreview(null);
    } catch (e: unknown) {
      setProfileError(e instanceof Error ? e.message : 'Failed to update profile. Please try again.');
    } finally {
      setIsSavingProfile(false);
    }
  }, [profileName, avatarFile, avatarPreview, fetchUser]);

  // Auto-open modal when navigated from header button
  useEffect(() => {
    if (location.state?.openAddModal) {
      setShowAddPropertyModal(true);
      // Clear the state so it doesn't reopen on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  // Real user data from auth store
  const userData = {
    name: user?.name ?? "—",
    email: user?.email ?? "—",
    phone: user?.phone ?? null,
    avatarInitial: (user?.name ?? "?").charAt(0).toUpperCase(),
    memberSince: user?.createdAt ? formatMemberSince(user.createdAt) : null,
    avatarUrl: user?.avatarUrl ?? null,
  };

  const ownedProperties = properties.filter(p => p.accessRole === 'owner');
  const stats = [
    { label: t('stats.homesOwned'), value: String(ownedProperties.length) },
    { label: t('stats.totalHomes'), value: String(properties.length) },
    { label: t('stats.active'), value: currentProperty ? "1" : "0" },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-secondary/30">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
          <h1 className="text-4xl font-light tracking-tight">{t('title')}</h1>
          <p className="text-muted-foreground mt-2">
            {t('subtitle')}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column - Personal Information */}
          <div className="space-y-6">
            {/* Avatar & Basic Info */}
            <Card className="p-6">
              <div className="flex items-start justify-between mb-6">
                <h2 className="text-xl font-normal">{t('personalInfo')}</h2>
                {!isEditingProfile ? (
                  <button
                    onClick={handleEditProfile}
                    className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    {t('edit')}
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleCancelEdit}
                      disabled={isSavingProfile}
                      className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                    >
                      <X className="h-3.5 w-3.5" />
                      {t('cancel')}
                    </button>
                    <button
                      onClick={handleSaveProfile}
                      disabled={isSavingProfile}
                      className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg bg-foreground text-background hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                      {isSavingProfile ? (
                        <span className="h-3.5 w-3.5 border-2 border-background/40 border-t-background rounded-full animate-spin" />
                      ) : null}
                      {isSavingProfile ? t('saving') : t('save')}
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                {/* Avatar */}
                <div className="flex items-center gap-4">
                  <div className="relative shrink-0">
                    {avatarPreview || userData.avatarUrl ? (
                      <img
                        src={avatarPreview ?? userData.avatarUrl!}
                        alt={t('photo.alt')}
                        className="h-20 w-20 rounded-full object-cover border"
                      />
                    ) : (
                      <div className="h-20 w-20 rounded-full bg-foreground text-background flex items-center justify-center text-2xl font-normal">
                        {userData.avatarInitial}
                      </div>
                    )}
                    {isEditingProfile && (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="absolute bottom-0 right-0 h-7 w-7 rounded-full bg-foreground text-background border-2 border-background flex items-center justify-center hover:opacity-80 transition-opacity"
                        title={t('photo.changeTitle')}
                      >
                        <Camera className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleAvatarChange}
                    />
                  </div>
                  <div className="space-y-1">
                    {user?.authProvider === 'google' && (
                        <span className="text-xs px-2 py-1 rounded-full bg-secondary text-muted-foreground">{t('google')}</span>
                      )}
                      {userData.memberSince && (
                        <p className="text-sm text-muted-foreground">{t('memberSince', { date: userData.memberSince })}</p>
                    )}
                  </div>
                </div>

                {/* Error banner */}
                {profileError && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                    <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
                    <p className="text-sm text-destructive">{profileError}</p>
                  </div>
                )}

                {/* Form Fields */}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-1.5 flex items-center gap-2">
                      <UserIcon className="h-4 w-4 text-muted-foreground" />
                      {t('fullName')}
                    </label>
                    <Input
                      value={isEditingProfile ? profileName : userData.name}
                      onChange={(e) => { setProfileName(e.target.value); setProfileNameError(''); }}
                      disabled={!isEditingProfile}
                      className="text-zinc-900"
                    />
                    {profileNameError && (
                      <p className="text-xs text-destructive mt-1">{profileNameError}</p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1.5 flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      {t('emailAddress')}
                    </label>
                    <div className="relative">
                      <Input
                        type="email"
                        value={userData.email}
                        disabled={true}
                        className="pr-16"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider text-muted-foreground pointer-events-none">{t('locked')}</span>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1.5 flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      {t('cellPhone')}
                    </label>

                    {/* Step 0 — Display current phone (read-only, not editing) */}
                    {phoneStep === 0 && (
                      <Input
                        type="tel"
                        value={userData.phone ?? "—"}
                        disabled={true}
                        className="text-zinc-900"
                      />
                    )}

                    {/* Step 1 — Enter new phone */}
                    {phoneStep === 1 && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Input
                            type="tel"
                            value={newPhone}
                            onChange={(e) => { setNewPhone(e.target.value); setPhoneError(''); }}
                            placeholder={t('phone.enterNew')}
                            className="flex-1 text-zinc-900 placeholder:text-zinc-400"
                            autoFocus
                          />
                          <button
                            onClick={handleRequestPhone}
                            disabled={phoneSaving}
                            className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg bg-foreground text-background hover:opacity-90 transition-opacity disabled:opacity-50 shrink-0"
                          >
                            {phoneSaving ? (
                              <span className="h-3.5 w-3.5 border-2 border-background/40 border-t-background rounded-full animate-spin" />
                            ) : null}
                            {t('phone.sendOtp')}
                          </button>
                          <button
                            onClick={handleCancelPhone}
                            disabled={phoneSaving}
                            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 shrink-0"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        {phoneError && (
                          <p className="text-xs text-destructive flex items-center gap-1">
                            <AlertCircle className="h-3 w-3 shrink-0" />
                            {phoneError}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Step 2 — Enter OTP */}
                    {phoneStep === 2 && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="relative flex-1">
                            <Input
                              type="text"
                              inputMode="numeric"
                              maxLength={6}
                              value={phoneOtp}
                              onChange={(e) => { setPhoneOtp(e.target.value.replace(/\D/g, '')); setPhoneError(''); }}
                              placeholder={t('phone.enterOtp')}
                              className="text-center tracking-[0.3em] text-zinc-900 placeholder:text-zinc-400 placeholder:tracking-normal"
                              autoFocus
                            />
                            {phoneOtp.length === 6 && (
                              <ShieldCheck className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                            )}
                          </div>
                          <button
                            onClick={handleVerifyPhone}
                            disabled={phoneSaving || phoneOtp.length < 4}
                            className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg bg-foreground text-background hover:opacity-90 transition-opacity disabled:opacity-50 shrink-0"
                          >
                            {phoneSaving ? (
                              <span className="h-3.5 w-3.5 border-2 border-background/40 border-t-background rounded-full animate-spin" />
                            ) : (
                              <Check className="h-3.5 w-3.5" />
                            )}
                            {t('phone.verify')}
                          </button>
                          <button
                            onClick={handleCancelPhone}
                            disabled={phoneSaving}
                            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 shrink-0"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {t('phone.otpSentFor')} <span className="font-medium text-foreground">{newPhone}</span>.{' '}
                          <button
                            onClick={() => { setPhoneStep(1); setPhoneOtp(''); setPhoneError(''); }}
                            className="underline hover:no-underline"
                          >
                            {t('phone.changeNumber')}
                          </button>
                        </p>
                        {phoneError && (
                          <p className="text-xs text-destructive flex items-center gap-1">
                            <AlertCircle className="h-3 w-3 shrink-0" />
                            {phoneError}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Right Column - Properties & Stats */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <Card className="p-6">
              <h2 className="text-xl font-normal mb-6">{t('quickStats')}</h2>
              <div className="grid grid-cols-2 gap-4">
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className="p-4 rounded-lg border bg-secondary/30"
                  >
                    <div className="text-2xl font-normal">{stat.value}</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Home Management */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-normal">{t('myHomes')}</h2>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="gap-2"
                  onClick={openAddModal}
                >
                  <Plus className="h-4 w-4" />
                  {t('addHome')}
                </Button>
              </div>

              {/* My Homes */}
              {ownedProperties.length > 0 && (
                <div className="mb-4">
                  <div className="space-y-3">
                    {ownedProperties.map((property) => {
                      const isActive = currentProperty?.id === property.id;
                      return (
                        <div
                          key={property.id}
                          onClick={() => switchProperty(property.id)}
                          className={cn(
                            "p-3.5 rounded-lg border cursor-pointer transition-all",
                            isActive ? "border-foreground bg-secondary/50" : "bg-background hover:bg-secondary"
                          )}
                        >
                          <div className="flex items-start gap-3">
                            <Home className="h-4 w-4 flex-shrink-0 text-amber-500 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-normal text-sm">{property.name}</span>
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                                  Owner
                                </span>
                              </div>
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {property.address}, {property.city}{property.state ? `, ${property.state}` : ""} {property.zip}
                              </div>
                              {property.assetCount != null && property.assetCount > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">
                                    {property.assetCount} asset{property.assetCount !== 1 ? "s" : ""}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {isActive && <Check className="h-3.5 w-3.5 text-green-500" />}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingHome(property);
                                }}
                                className="p-1 rounded-full hover:bg-secondary transition-colors"
                                title="Edit"
                              >
                                <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeletingHomeId(property.id);
                                  setDeleteConfirm(true);
                                }}
                                className="p-1 rounded-full hover:bg-destructive/10 transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="h-3.5 w-3.5 text-destructive/70" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {properties.length === 0 && (
                <div className="text-center py-8">
                  <Home className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                  <h3 className="text-lg font-medium mb-2">{t('noHomes.title')}</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('noHomes.desc')}
                  </p>
                  <Button onClick={openAddModal} className="gap-2">
                    <Plus className="h-4 w-4" />
                    {t('noHomes.cta')}
                  </Button>
                </div>
              )}

              {/* Add Property Card */}
              {ownedProperties.length > 0 && (
                <button
                  onClick={openAddModal}
                  className={cn(
                    "w-full mt-3 p-4 rounded-lg border-2 border-dashed",
                    "hover:bg-secondary transition-colors duration-200",
                    "flex items-center justify-center gap-2 text-muted-foreground"
                  )}
                >
                  <Plus className="h-5 w-5" />
                  <span className="font-medium">{t('addNewProperty')}</span>
                </button>
              )}
            </Card>
          </div>
        </div>
      </div>

      {/* Add Property Modal */}
      {showAddPropertyModal && (
        <AddPropertyModal onClose={closeAddModal} onSuccess={refreshProperties} />
      )}

      {/* Edit Home Modal */}
      {editingHome && (
        <EditHomeModal
          home={editingHome}
          onClose={() => setEditingHome(null)}
          onSuccess={async () => {
            setEditingHome(null);
            await refreshProperties();
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && deletingHomeId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="bg-background border rounded-2xl shadow-lg w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-medium mb-2">Delete Home</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Are you sure? This home and all its data will be permanently deleted for you and all family members.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => { setDeleteConfirm(false); setDeletingHomeId(null); }}
                disabled={deleting}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  setDeleting(true);
                  try {
                    await deleteHome(deletingHomeId);
                    setDeleteConfirm(false);
                    setDeletingHomeId(null);
                    await refreshProperties();
                  } catch (err) {
                    console.error("Failed to delete home:", err);
                  } finally {
                    setDeleting(false);
                  }
                }}
                disabled={deleting}
                className="flex-1 bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleting ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
